import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/redux/store';
import { fetchNotificationCount } from '@/api/notificationApi';

interface NotificationBadgeProps {
    className?: string;
}

const NotificationBadge: React.FC<NotificationBadgeProps> = ({ className = "" }) => {
    const [count, setCount] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(true);
    const user = useSelector((state: RootState) => state.user.user);

    const loadNotificationCount = async () => {
        if (!user?.id) {
            setCount(0);
            setLoading(false);
            return;
        }

        try {
            const notificationCount = await fetchNotificationCount();
            setCount(notificationCount);
        } catch (error) {
            console.error('Error fetching notification count:', error);
            setCount(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadNotificationCount();
    }, [user?.id]);

    // Listen for custom events to refresh the count
    useEffect(() => {
        const handleNotificationChange = () => {
            loadNotificationCount();
        };

        // Listen for custom events when notifications are added or deleted
        window.addEventListener('notificationAdded', handleNotificationChange);
        window.addEventListener('notificationDeleted', handleNotificationChange);

        return () => {
            window.removeEventListener('notificationAdded', handleNotificationChange);
            window.removeEventListener('notificationDeleted', handleNotificationChange);
        };
    }, [user?.id]);

    if (loading) {
        return null;
    }

    if (count === 0) {
        return null;
    }

    return (
        <div className={`absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 ${className}`}>
            {count > 99 ? '99+' : count}
        </div>
    );
};

export default NotificationBadge;
